import { useState, useRef } from 'react';
import {
    Box, Typography, Dialog, DialogTitle, DialogContent, DialogActions, Button, IconButton,
    ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import PrintIcon from '@mui/icons-material/Print';
import html2canvas from 'html2canvas';

/**
 * RSC Advanced — 10-Year Savings Comparison body.
 *
 * Mirrors the AsternResidentialSalesSystem "10年节省对比分析 / 10-Year Savings
 * Comparison" table: a 现金/分期付款 (Cash/Installment) payment toggle drives a
 * metric × column grid (No-Solar + one column per selected plan), ending in a
 * highlighted-green 10-year net-savings row, plus a yellow depreciation note.
 *
 * NO new tariff math — reuses ONLY already-computed inputs:
 *   - monthlySaving / netCash / cc[36] per plan, the monthly bill, and the
 *     engine's pure ccFromCash for the 0% 36-month installment total.
 *
 * Row formulas (per plan column):
 *   - 每月电费:        No-Solar = bill;            With-Solar = bill − monthlySaving
 *   - 每年电费:        ×12 of the above
 *   - 系统价格:        No-Solar = "—";             With-Solar = systemPrice
 *   - 10年累计电费:    No-Solar = bill×120;        With-Solar = newBill×120
 *   - 10年总支出:      No-Solar = bill×120;        With-Solar = newBill×120 + systemPrice
 *   - 10年净节省:      No-Solar = "—";             With-Solar = bill×120 − (newBill×120 + systemPrice)
 *
 * systemPrice switches with the toggle:
 *   - cash:        plan.netCash
 *   - installment: plan.cc?.[36]  (else ccFromCash(plan.netCash, 36)) — the 0% 36-mo total.
 *
 * Props: { picks, letters, bill, ccFromCash, t, poppins }
 *   letters defaults to A/B/C… by index when not supplied.
 */
export function TenYearComparisonBody({ picks = [], letters = [], bill = 0, ccFromCash, t, poppins }) {
    const [mode, setMode] = useState('cash'); // 'cash' | 'installment'
    const rm = (v) => `RM ${(Number(v) || 0).toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    const MONTHS = 120;

    const monthly = Number(bill) || 0;

    // systemPrice per plan, switched by the payment toggle.
    const sysPrice = (plan) => {
        if (mode === 'installment') {
            const cc36 = plan.cc && plan.cc[36] != null ? plan.cc[36] : (ccFromCash ? ccFromCash(plan.netCash, 36) : 0);
            return Number(cc36) || 0;
        }
        return Number(plan.netCash) || 0;
    };

    const letterFor = (i) => letters[i] || String.fromCharCode(65 + i);
    const single = picks.length === 1;
    const modeLabel = mode === 'cash' ? t('现金', 'Cash', 'Tunai') : t('分期', 'Installment', 'Ansuran');

    // Per-plan derived figures.
    const cols = picks.map((plan) => {
        const monthlySaving = Number(plan.monthlySaving) || 0;
        const newBill = monthly - monthlySaving;
        const systemPrice = sysPrice(plan);
        const noSolarCumulative = monthly * MONTHS;
        const withSolarCumulative = newBill * MONTHS;
        const withSolarTotal = withSolarCumulative + systemPrice;
        const netSavings = noSolarCumulative - withSolarTotal;
        return { plan, newBill, systemPrice, withSolarCumulative, withSolarTotal, netSavings };
    });

    const noSolarCumulative = monthly * MONTHS;

    // Shared cell styles.
    const HEAD = '#0F62CC';
    const HEAD2 = '#0a4a9e';
    const headCell = {
        p: '12px 14px', fontFamily: poppins, fontWeight: 800, fontSize: '0.8rem',
        color: '#fff', textAlign: 'center', lineHeight: 1.25,
    };
    const labelCell = {
        p: '11px 14px', fontFamily: poppins, fontWeight: 700, fontSize: '0.78rem',
        color: '#1E293B', borderBottom: '1px solid #E5E7EB', textAlign: 'left',
        whiteSpace: 'nowrap',
    };
    const valCell = (highlight = false) => ({
        p: '11px 14px', fontFamily: "'JetBrains Mono', monospace", fontSize: '0.78rem',
        color: '#334155', borderBottom: '1px solid #E5E7EB', textAlign: 'center',
        bgcolor: highlight ? '#EFF6FF' : 'transparent',
    });

    return (
        <>
            {picks.length === 0 ? (
                <Typography sx={{ fontFamily: poppins, color: '#64748B' }}>
                    {t('暂无方案', 'No plans yet', 'Tiada pelan')}
                </Typography>
            ) : (
                <>
                    {/* Payment-method toggle */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.75, flexWrap: 'wrap' }}>
                        <Typography sx={{ fontFamily: poppins, fontWeight: 700, fontSize: '0.82rem', color: '#475569' }}>
                            {t('付款方式', 'Payment method', 'Cara bayaran')}:
                        </Typography>
                        <ToggleButtonGroup
                            exclusive
                            size="small"
                            value={mode}
                            onChange={(_e, v) => { if (v) setMode(v); }}
                            sx={{
                                '& .MuiToggleButton-root': {
                                    fontFamily: poppins, fontWeight: 700, fontSize: '0.76rem',
                                    textTransform: 'none', px: 2, py: 0.5, borderColor: '#BFDBFE',
                                    color: '#0F62CC',
                                },
                                '& .Mui-selected': {
                                    bgcolor: '#0F62CC !important', color: '#fff !important',
                                    '&:hover': { bgcolor: '#0d4ea3 !important' },
                                },
                            }}
                        >
                            <ToggleButton value="cash">{t('现金', 'Cash', 'Tunai')}</ToggleButton>
                            <ToggleButton value="installment">{t('分期付款', 'Installment', 'Ansuran')}</ToggleButton>
                        </ToggleButtonGroup>
                    </Box>

                    {/* Comparison table */}
                    <Box sx={{ overflowX: 'auto', borderRadius: 2, border: '1px solid #E5E7EB' }}>
                        <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', minWidth: 460 }}>
                            <Box component="thead" sx={{ background: `linear-gradient(135deg, ${HEAD}, ${HEAD2})` }}>
                                <Box component="tr">
                                    <Box component="th" sx={{ ...headCell, textAlign: 'left' }}>
                                        {t('指标', 'Metric', 'Metrik')}
                                    </Box>
                                    <Box component="th" sx={headCell}>
                                        {t('不装太阳能', 'No Solar', 'Tiada Solar')}
                                    </Box>
                                    {cols.map((c, i) => (
                                        <Box component="th" key={c.plan.key} sx={headCell}>
                                            {single
                                                ? t(`装太阳能 (${modeLabel})`, `With Solar (${modeLabel})`, `Dengan Solar (${modeLabel})`)
                                                : t(`装太阳能 ${letterFor(i)} (${modeLabel})`,
                                                    `With Solar ${letterFor(i)} (${modeLabel})`,
                                                    `Dengan Solar ${letterFor(i)} (${modeLabel})`)}
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                            <Box component="tbody">
                                {/* 每月电费 */}
                                <Box component="tr">
                                    <Box component="td" sx={labelCell}>{t('每月电费', 'Monthly bill', 'Bil bulanan')}</Box>
                                    <Box component="td" sx={valCell(false)}>{rm(monthly)}</Box>
                                    {cols.map((c) => (
                                        <Box component="td" key={c.plan.key} sx={valCell(true)}>{rm(c.newBill)}</Box>
                                    ))}
                                </Box>
                                {/* 每年电费 */}
                                <Box component="tr">
                                    <Box component="td" sx={labelCell}>{t('每年电费', 'Yearly bill', 'Bil tahunan')}</Box>
                                    <Box component="td" sx={valCell(false)}>{rm(monthly * 12)}</Box>
                                    {cols.map((c) => (
                                        <Box component="td" key={c.plan.key} sx={valCell(true)}>{rm(c.newBill * 12)}</Box>
                                    ))}
                                </Box>
                                {/* separator */}
                                <Box component="tr">
                                    <Box component="td" colSpan={2 + cols.length}
                                        sx={{ p: '6px', bgcolor: '#F3F4F6', borderBottom: '2px solid #D1D5DB' }} />
                                </Box>
                                {/* 系统价格 */}
                                <Box component="tr">
                                    <Box component="td" sx={labelCell}>{t('系统价格', 'System price', 'Harga sistem')}</Box>
                                    <Box component="td" sx={valCell(false)}>—</Box>
                                    {cols.map((c) => (
                                        <Box component="td" key={c.plan.key} sx={valCell(true)}>{rm(c.systemPrice)}</Box>
                                    ))}
                                </Box>
                                {/* 10年累计电费 */}
                                <Box component="tr">
                                    <Box component="td" sx={labelCell}>{t('10年累计电费', '10-yr cumulative bill', 'Bil terkumpul 10thn')}</Box>
                                    <Box component="td" sx={valCell(false)}>{rm(noSolarCumulative)}</Box>
                                    {cols.map((c) => (
                                        <Box component="td" key={c.plan.key} sx={valCell(true)}>{rm(c.withSolarCumulative)}</Box>
                                    ))}
                                </Box>
                                {/* 10年总支出 */}
                                <Box component="tr">
                                    <Box component="td" sx={labelCell}>{t('10年总支出', '10-yr total expenditure', 'Perbelanjaan 10thn')}</Box>
                                    <Box component="td" sx={valCell(false)}>{rm(noSolarCumulative)}</Box>
                                    {cols.map((c) => (
                                        <Box component="td" key={c.plan.key} sx={valCell(true)}>{rm(c.withSolarTotal)}</Box>
                                    ))}
                                </Box>
                                {/* 10年净节省 — highlighted green */}
                                <Box component="tr" sx={{ bgcolor: '#F0FDF4', borderTop: '2px solid #10B981' }}>
                                    <Box component="td" sx={{ ...labelCell, color: '#065F46', borderBottom: 'none' }}>
                                        {t('10年净节省', '10-yr net savings', 'Jimatan bersih 10thn')}
                                    </Box>
                                    <Box component="td" sx={{ ...valCell(false), borderBottom: 'none' }}>—</Box>
                                    {cols.map((c) => (
                                        <Box component="td" key={c.plan.key}
                                            sx={{
                                                p: '13px 14px', fontFamily: "'JetBrains Mono', monospace",
                                                fontSize: '0.86rem', fontWeight: 800, color: '#10B981',
                                                textAlign: 'center', borderBottom: 'none', bgcolor: '#ECFDF5',
                                            }}>
                                            {rm(c.netSavings)}
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        </Box>
                    </Box>

                    {/* Yellow depreciation note */}
                    <Box sx={{ mt: 2, p: '14px 18px', bgcolor: '#FEF3C7', borderLeft: '4px solid #F59E0B', borderRadius: 2 }}>
                        <Typography sx={{ fontFamily: poppins, fontStyle: 'italic', fontSize: '0.74rem', lineHeight: 1.6, color: '#92400E' }}>
                            {t(
                                '注：买车会贬值，装太阳能是赚钱。这笔电费就算不装系统也会送给 TNB，且未计算未来电费起价。10 年后系统继续为您省钱。',
                                'Note: Cars depreciate, solar appreciates. This bill is paid to TNB anyway without solar, and future tariff hikes are not even counted. The system keeps saving you money beyond 10 years.',
                                'Nota: Kereta susut nilai, solar menjana wang. Bil ini tetap dibayar kepada TNB tanpa solar, dan kenaikan tarif masa depan belum dikira. Sistem terus menjimatkan selepas 10 tahun.'
                            )}
                        </Typography>
                    </Box>
                </>
            )}
        </>
    );
}

export default function TenYearComparisonModal({ open, onClose, picks = [], letters = [], bill = 0, ccFromCash, t, poppins }) {
    const captureRef = useRef(null);
    // Render the table to a canvas (white bg, 2× for crisp text).
    const capture = async () => {
        if (!captureRef.current) return null;
        return html2canvas(captureRef.current, { scale: 2, backgroundColor: '#ffffff', useCORS: true });
    };
    const downloadImage = async () => {
        const canvas = await capture();
        if (!canvas) return;
        const link = document.createElement('a');
        link.download = '10-Year-Comparison.png';
        link.href = canvas.toDataURL('image/png');
        link.click();
    };
    const printImage = async () => {
        const canvas = await capture();
        if (!canvas) return;
        const img = canvas.toDataURL('image/png');
        const w = window.open('', '_blank');
        if (!w) return;
        w.document.write(`<html><head><title>10-Year Comparison</title><style>@media print{@page{margin:10mm}}body{margin:0}img{width:100%}</style></head><body><img src="${img}" onload="window.focus();window.print();"/></body></html>`);
        w.document.close();
    };
    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle sx={{ fontFamily: poppins, fontWeight: 800, fontSize: '1rem', pr: 6 }}>
                {t('10年节省对比分析', '10-Year Savings Comparison', 'Analisis Perbandingan Jimatan 10 Tahun')}
                <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}><CloseIcon /></IconButton>
            </DialogTitle>
            <DialogContent dividers>
                <Box ref={captureRef} sx={{ bgcolor: '#fff', p: 1 }}>
                    <TenYearComparisonBody picks={picks} letters={letters} bill={bill} ccFromCash={ccFromCash} t={t} poppins={poppins} />
                </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 1.5, gap: 1 }}>
                <Button onClick={downloadImage} variant="outlined" startIcon={<DownloadIcon sx={{ fontSize: '1rem' }} />}
                    sx={{ fontFamily: poppins, fontSize: '0.78rem', fontWeight: 700, textTransform: 'none', borderRadius: '8px', borderColor: '#0F62CC', color: '#0F62CC', '&:hover': { bgcolor: '#0F62CC10', borderColor: '#0F62CC' } }}>
                    {t('下载图片', 'Download Image', 'Muat Turun Imej')}
                </Button>
                <Button onClick={printImage} variant="contained" startIcon={<PrintIcon sx={{ fontSize: '1rem' }} />}
                    sx={{ fontFamily: poppins, fontSize: '0.78rem', fontWeight: 700, textTransform: 'none', borderRadius: '8px', boxShadow: 'none', bgcolor: '#0F62CC', '&:hover': { bgcolor: '#0d4ea3' } }}>
                    {t('打印', 'Print', 'Cetak')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
